import type { BehaviorPropertySnapshotSource } from '~/composables/useBehaviorEventTracking';

import { useBehaviorEventTracking } from '~/composables/useBehaviorEventTracking';
import { useTrackingIdentity } from '~/composables/visitorIdentity';
import { nuxtLeadsRepository } from '~~/adapters/repositories/nuxt/nuxtLeadsRepository';

import { extractErrorLogContext } from '../useLogger';
import { useLeadFlowLogger } from './useLeadFlowLogger';

export interface LeadData {
	name: string;
	phone: string;
	email?: string;
	message?: string;
}

export type PropertyLeadData = LeadData & {
	adCampaign?: Record<string, string>;
	property?: BehaviorPropertySnapshotSource | null;
	/** Commercial id for behavior-events `propertyId` (not Mongo `_id`). */
	commercialId?: string | null;
} & (
		| { propertyId: string; agentSlug?: never }
		| { agentSlug: string; propertyId?: never }
	);

export interface CondoLeadData extends LeadData {
	condoId: string;
	condoName: string;
	companyDistribution: string;
	adCampaign?: Record<string, string>;
}

export interface LeadSubmissionResult {
	success: boolean;
	leadId?: string;
	error?: string;
}

export function useLeadService() {
	const logger = useLeadFlowLogger('LeadService');
	const getTrackingIdentity = useTrackingIdentity();
	const { trackLeadCreated } = useBehaviorEventTracking();

	async function createPropertyLead(
		data: PropertyLeadData
	): Promise<LeadSubmissionResult> {
		logger.info('Iniciando criação de lead de imóvel', {
			propertyId: data.propertyId,
			hasEmail: !!data.email,
			hasMessage: !!data.message,
			adCampaignKeys: Object.keys(data.adCampaign || {})
		});

		try {
			const [result, error] = await nuxtLeadsRepository.createPropertyLead({
				comments: data.message || '',
				...(data.propertyId
					? { propertyId: data.propertyId }
					: { agentSlug: data.agentSlug! }),
				email: data.email || null,
				name: data.name,
				phoneNumber: data.phone,
				...getTrackingIdentity(),
				...(data.adCampaign || {})
			});

			if (error) {
				logger.warn('Falha ao criar lead de imóvel (API retornou erro)', {
					propertyId: data.propertyId,
					name: data.name,
					phone: data.phone,
					errorSource: error.source,
					errorMessage: error.message,
					errorStatusCode: (
						error.originalErrorObject as { statusCode?: number }
					)?.statusCode,
					errorStatusMessage: (
						error.originalErrorObject as { statusMessage?: string }
					)?.statusMessage
				});
				return {
					success: false,
					error: error.message || 'Erro ao criar lead de imóvel'
				};
			}

			const leadId = result?.leadId;
			logger.info('Lead de imóvel criado com sucesso', {
				propertyId: data.propertyId,
				leadId
			});

			if (data.propertyId && leadId) {
				const behaviorPropertyId = data.commercialId?.trim();
				if (behaviorPropertyId) {
					trackLeadCreated({
						propertyId: behaviorPropertyId,
						leadId,
						source: 'property_page',
						property: data.property
					});
				} else {
					logger.warn('Lead criado sem commercialId — behavior event omitido', {
						propertyId: data.propertyId,
						leadId
					});
				}
			} else if (data.propertyId && !leadId) {
				logger.warn('Lead criado sem leadId — behavior event omitido', {
					propertyId: data.propertyId
				});
			}

			return { success: true, leadId };
		} catch (error) {
			logger.error('Erro inesperado ao criar lead de imóvel', {
				propertyId: data.propertyId,
				name: data.name,
				phone: data.phone,
				...extractErrorLogContext(error)
			});
			return {
				success: false,
				error: 'Erro inesperado ao criar lead de imóvel'
			};
		}
	}

	async function createCondoLead(
		data: CondoLeadData
	): Promise<LeadSubmissionResult> {
		logger.info('Iniciando criação de lead de condomínio', {
			condoId: data.condoId,
			condoName: data.condoName,
			companyDistribution: data.companyDistribution,
			hasEmail: !!data.email,
			hasMessage: !!data.message,
			adCampaignKeys: Object.keys(data.adCampaign || {})
		});

		try {
			const [result, error] = await nuxtLeadsRepository.createCondoLead({
				comments: data.message || '',
				canonicalCondoId: data.condoId,
				companyDistribution: data.companyDistribution,
				canonicalCondoName: data.condoName,
				email: data.email || null,
				name: data.name,
				phoneNumber: data.phone,
				...getTrackingIdentity(),
				...(data.adCampaign || {})
			});

			if (error) {
				logger.warn('Falha ao criar lead de condomínio (API retornou erro)', {
					condoId: data.condoId,
					condoName: data.condoName,
					name: data.name,
					phone: data.phone,
					errorSource: error.source,
					errorMessage: error.message,
					errorStatusCode: (
						error.originalErrorObject as { statusCode?: number }
					)?.statusCode,
					errorStatusMessage: (
						error.originalErrorObject as { statusMessage?: string }
					)?.statusMessage
				});
				return {
					success: false,
					error: error.message || 'Erro ao criar lead de condomínio'
				};
			}

			logger.info('Lead de condomínio criado com sucesso', {
				condoId: data.condoId,
				condoName: data.condoName,
				leadId: result?.leadId
			});
			return { success: true, leadId: result?.leadId };
		} catch (error) {
			logger.error('Erro inesperado ao criar lead de condomínio', {
				condoId: data.condoId,
				condoName: data.condoName,
				name: data.name,
				phone: data.phone,
				...extractErrorLogContext(error)
			});
			return {
				success: false,
				error: 'Erro inesperado ao criar lead de condomínio'
			};
		}
	}

	return {
		createPropertyLead,
		createCondoLead
	};
}
